import React, { useCallback, useEffect, useMemo, useState } from "react";

import { AlertTriangle, Building2, Loader2 } from "lucide-react";

import { errorText, formatDateTime } from "./format";
import OrgSelect from "./OrgSelect";
import {
  type PipelineHealth,
  type RunDiff,
  type RunDiffAccount,
  type RunDiffCard,
  type RunDiffNearMiss,
  type RunSummary,
  type SignalAdminOrganization,
  type SignalTier,
  TIER_META,
} from "./types";
import { useAppDispatch } from "../../Store/Hooks";
import { fetchSignalAdminRunDiff } from "../../Store/Reducers/SignalAdminSlice";

interface RunDiffPanelProps {
  orgId: string;
  organizations: SignalAdminOrganization[];
  orgsLoading: boolean;
  onOrgChange: (orgId: string) => void;
  /** Pipeline health for the selected organization; absent until loaded. */
  health?: PipelineHealth;
}

/** Red or amber strip naming what is wrong with the org's pipeline. */
export const HealthBanner: React.FC<{ health?: PipelineHealth }> = ({
  health,
}) => {
  if (!health || health.status === "ok") return null;
  const alert = health.status === "alert";
  return (
    <div
      role="status"
      className={`flex items-start gap-2 rounded-md border px-3 py-2 text-[12.5px] ${
        alert
          ? "border-red-200 bg-red-50 text-red-800"
          : "border-amber-200 bg-amber-50 text-amber-800"
      }`}
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="min-w-0">
        <div className="font-semibold">
          {alert
            ? "Signal pipeline needs attention now"
            : "Signal pipeline warning"}
        </div>
        <ul className="m-0 mt-0.5 list-disc pl-4">
          {health.reasons.map(r => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

type Mode = "back-to-back" | "any-two";
type Column = 0 | 1 | 2 | 3 | 4;

interface FlowNode {
  id: string;
  column: Column;
  title: string;
  sub: string;
  tone: "cause" | "plain" | "new" | "cleared" | "covered" | "critical";
  chains: string[];
  detail: [string, string][];
}

interface FlowEdge {
  from: string;
  to: string;
  chains: string[];
}

/** Accounts drawn in the diagram; the table below lists every one. */
const MAX_DIAGRAM_ACCOUNTS = 15;
const COLUMN_X = [16, 236, 456, 676, 896];
const COLUMN_W = [200, 200, 200, 200, 160];
const COLUMN_TITLES = [
  "CAUSE",
  "WHAT CHANGED",
  "RULE OUTCOME",
  "CARD",
  "WHO SEES IT",
];
const ROW_H = 54;
const NODE_H = 44;

const PERSONA_LABEL: Record<string, string> = {
  ae: "AE",
  csm: "CSM",
  sponsor: "Sponsor",
};

const show = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) return value.join(", ") || "none";
  if (typeof value === "number")
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
  const text = String(value);
  // ISO timestamps read as dates.
  return /^\d{4}-\d{2}-\d{2}T/.test(text) ? formatDateTime(text) : text;
};

const timeOf = (run: RunSummary | null, withSeconds = false): string =>
  run
    ? new Date(run.started_at).toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
        ...(withSeconds ? { second: "2-digit" as const } : {}),
      })
    : "—";

/** Runs that share a minute show seconds so the chips can be told apart. */
const sharesMinute = (runs: RunSummary[], run: RunSummary): boolean =>
  runs.filter(r => timeOf(r) === timeOf(run)).length > 1;

const plural = (n: number, word: string): string =>
  `${n} ${word}${n === 1 ? "" : "s"}`;

const CARD_CHANGE_LABEL: Record<RunDiffCard["change"], string> = {
  new: "New",
  cleared: "Cleared",
  covered: "Covered",
  hidden: "Hidden",
  tier: "Tier changed",
};

const tierLabel = (tier?: SignalTier | null): string =>
  tier ? TIER_META[tier].label : "";

/** "Critical", or "Warning → Critical" for a card whose tier changed. */
const cardTierText = (card: RunDiffCard): string =>
  card.change === "tier" && card.tier_before
    ? `${tierLabel(card.tier_before)} → ${tierLabel(card.tier)}`
    : tierLabel(card.tier);

/** Build the five-column diagram from the diff. */
export const buildFlow = (
  diff: RunDiff
): { nodes: FlowNode[]; edges: FlowEdge[]; hidden: number } => {
  const accounts = diff.accounts.slice(0, MAX_DIAGRAM_ACCOUNTS);
  const nodes: FlowNode[] = [];
  const edges: FlowEdge[] = [];
  const chainOf = (a: RunDiffAccount) => `a:${a.account_id}`;

  diff.causes.forEach(cause => {
    const chains = accounts
      .filter(a => a.cause_ids.includes(cause.id))
      .map(chainOf);
    if (!chains.length) return;
    nodes.push({
      id: `c:${cause.id}`,
      column: 0,
      title: cause.label,
      sub: plural(chains.length, "account") + " changed",
      tone: "cause",
      chains,
      detail: Object.entries(cause.detail).map(([k, v]) => [k, show(v)]),
    });
  });

  const personaChains: Record<string, string[]> = {};
  const personaDelta: Record<string, { users: number; delta: number }> = {};

  accounts.forEach(account => {
    const chain = chainOf(account);
    const changeId = `x:${account.account_id}`;
    const firstChange = account.input_changes[0];
    const thresholdOnly = !firstChange && diff.threshold_changes.length > 0;
    nodes.push({
      id: changeId,
      column: 1,
      title: account.account_name,
      sub: firstChange
        ? `${firstChange.label}: ${show(firstChange.before)} → ${show(firstChange.after)}`
        : thresholdOnly
          ? `${diff.threshold_changes[0].label}: ${show(diff.threshold_changes[0].before)} → ${show(diff.threshold_changes[0].after)}`
          : "No input change (time passed)",
      tone: "plain",
      chains: [chain],
      detail: [
        ["Account", account.account_name],
        ...account.input_changes.map(
          c =>
            [c.label, `${show(c.before)} → ${show(c.after)}`] as [
              string,
              string,
            ]
        ),
        ...(thresholdOnly
          ? diff.threshold_changes.map(
              c =>
                [c.label, `${show(c.before)} → ${show(c.after)}`] as [
                  string,
                  string,
                ]
            )
          : []),
      ],
    });
    account.cause_ids.forEach(cid =>
      edges.push({ from: `c:${cid}`, to: changeId, chains: [chain] })
    );

    account.flips.forEach(flip => {
      const flipId = `f:${account.account_id}:${flip.rule_id}`;
      nodes.push({
        id: flipId,
        column: 2,
        title: flip.title,
        sub: `${flip.before_label} → ${flip.after_label}`,
        tone: "plain",
        chains: [chain],
        detail: [
          ["Rule", flip.title],
          ["Account", account.account_name],
          ["Condition", flip.condition ?? "—"],
          ["Before", flip.before_label],
          ["After", flip.after_label],
          ["Why", flip.explanation],
        ],
      });
      edges.push({ from: changeId, to: flipId, chains: [chain] });
    });

    account.cards.forEach(card => {
      const cardId = `k:${account.account_id}:${card.rule_id}`;
      const tone =
        card.change === "new" || card.change === "tier"
          ? card.tier === "critical"
            ? "critical"
            : card.change === "new"
              ? "new"
              : "plain"
          : card.change === "covered"
            ? "covered"
            : "cleared";
      const changeLabel = CARD_CHANGE_LABEL[card.change];
      nodes.push({
        id: cardId,
        column: 3,
        title: `${changeLabel} · ${cardTierText(card)}`,
        sub: card.title,
        tone,
        chains: [chain],
        detail: [
          ["Change", changeLabel],
          ["Signal", card.title],
          ["Account", account.account_name],
          ["Tier", cardTierText(card) || "—"],
          ...(card.covered_by
            ? ([["Covered by", card.covered_by]] as [string, string][])
            : []),
        ],
      });
      edges.push({
        from: `f:${account.account_id}:${card.rule_id}`,
        to: cardId,
        chains: [chain],
      });
      account.personas.forEach(p => {
        personaChains[p.persona] = Array.from(
          new Set([...(personaChains[p.persona] ?? []), chain])
        );
        edges.push({ from: cardId, to: `p:${p.persona}`, chains: [chain] });
      });
    });
    account.personas.forEach(p => {
      if (!account.cards.length) return;
      const slot = personaDelta[p.persona] ?? { users: 0, delta: 0 };
      slot.users += p.users;
      slot.delta += p.card_delta * p.users;
      personaDelta[p.persona] = slot;
    });
  });

  Object.entries(personaChains).forEach(([persona, chains]) => {
    // Distinct users come from the server; summing per account would count
    // one person on two accounts twice.
    const server = diff.summary?.personas?.find(p => p.persona === persona);
    const totals = server
      ? { users: server.users, delta: server.card_delta }
      : (personaDelta[persona] ?? { users: 0, delta: 0 });
    const delta =
      totals.delta > 0 ? `+${totals.delta}` : String(totals.delta || 0);
    nodes.push({
      id: `p:${persona}`,
      column: 4,
      title: PERSONA_LABEL[persona] ?? persona,
      sub: `${plural(totals.users, "user")} · ${delta} ${Math.abs(totals.delta) === 1 ? "card" : "cards"}`,
      tone: "plain",
      chains,
      detail: [
        ["Persona", PERSONA_LABEL[persona] ?? persona],
        ["Users", String(totals.users)],
        ["Cards, net", delta],
        ["Accounts", String(chains.length)],
      ],
    });
  });

  return {
    nodes,
    edges,
    hidden: Math.max(0, diff.accounts.length - accounts.length),
  };
};

const TONE_CLASS: Record<FlowNode["tone"], string> = {
  cause: "fill-white stroke-brand",
  plain: "fill-white stroke-gray-300",
  new: "fill-white stroke-emerald-500",
  critical: "fill-white stroke-red-500",
  cleared: "fill-white stroke-gray-400 [stroke-dasharray:3_3]",
  covered: "fill-white stroke-amber-500",
};

const FlowDiagram: React.FC<{ diff: RunDiff }> = ({ diff }) => {
  const { nodes, edges, hidden } = useMemo(() => buildFlow(diff), [diff]);
  const [active, setActive] = useState<FlowNode | null>(null);

  const positions = useMemo(() => {
    const rowByColumn = [0, 0, 0, 0, 0];
    const pos: Record<string, { x: number; y: number; w: number }> = {};
    nodes.forEach(node => {
      const row = rowByColumn[node.column]++;
      pos[node.id] = {
        x: COLUMN_X[node.column],
        y: 36 + row * ROW_H,
        w: COLUMN_W[node.column],
      };
    });
    return pos;
  }, [nodes]);

  const height =
    36 +
    Math.max(
      1,
      ...[0, 1, 2, 3, 4].map(c => nodes.filter(n => n.column === c).length)
    ) *
      ROW_H;
  const on = (chains: string[]) =>
    !active || chains.some(c => active.chains.includes(c));

  if (!nodes.length) {
    return (
      <p className="m-0 rounded-lg border border-dashed border-gray-200 p-4 text-center text-[12.5px] text-gray-500">
        No rule outcome changed between these runs.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-50">
        <svg
          viewBox={`0 0 1072 ${height}`}
          className="block h-auto w-full min-w-[960px]"
          role="img"
          aria-label="From causes to changed inputs to rule outcomes to cards to personas"
        >
          {COLUMN_TITLES.map((title, i) => (
            <text
              key={title}
              x={COLUMN_X[i]}
              y={20}
              className="fill-gray-400 text-[11px] font-semibold tracking-wider"
            >
              {title}
            </text>
          ))}
          {edges.map((edge, i) => {
            const a = positions[edge.from];
            const b = positions[edge.to];
            if (!a || !b) return null;
            const x1 = a.x + a.w;
            const y1 = a.y + NODE_H / 2;
            const x2 = b.x;
            const y2 = b.y + NODE_H / 2;
            const mid = (x1 + x2) / 2;
            const lit = active && on(edge.chains);
            return (
              <path
                key={`${edge.from}-${edge.to}-${i}`}
                d={`M${x1} ${y1} C${mid} ${y1},${mid} ${y2},${x2} ${y2}`}
                className={`fill-none ${lit ? "stroke-brand" : "stroke-gray-300"}`}
                strokeWidth={lit ? 2.2 : 1.4}
                opacity={on(edge.chains) ? 1 : 0.15}
              />
            );
          })}
          {nodes.map(node => {
            const p = positions[node.id];
            return (
              <g
                key={node.id}
                role="button"
                tabIndex={0}
                aria-label={`${node.title}: ${node.sub}`}
                className="cursor-pointer focus:outline-none"
                opacity={on(node.chains) ? 1 : 0.25}
                onClick={() =>
                  setActive(prev => (prev?.id === node.id ? null : node))
                }
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setActive(prev => (prev?.id === node.id ? null : node));
                  }
                }}
              >
                <rect
                  x={p.x}
                  y={p.y}
                  width={p.w}
                  height={NODE_H}
                  rx={6}
                  className={TONE_CLASS[node.tone]}
                  strokeWidth={active?.id === node.id ? 2.4 : 1.2}
                />
                <text
                  x={p.x + 10}
                  y={p.y + 18}
                  className="fill-gray-900 text-[12px] font-semibold"
                >
                  {node.title.length > 28
                    ? `${node.title.slice(0, 27)}…`
                    : node.title}
                </text>
                <text
                  x={p.x + 10}
                  y={p.y + 34}
                  className="fill-gray-500 text-[11px]"
                >
                  {node.sub.length > 32
                    ? `${node.sub.slice(0, 31)}…`
                    : node.sub}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
      <p className="m-0 text-[11.5px] text-gray-500">
        Click a box to trace its chain and see the record behind it. Click it
        again to clear.
        {hidden > 0 &&
          ` ${plural(hidden, "more account")} changed; see the table below.`}
      </p>
      {active && (
        <div
          className="rounded-lg border border-brand/40 bg-brand-faint p-3"
          aria-live="polite"
        >
          <h4 className="m-0 mb-2 text-[13px] font-semibold text-brand-dark">
            {active.title}
          </h4>
          <dl className="m-0 grid grid-cols-[minmax(120px,max-content)_minmax(0,1fr)] gap-x-3 gap-y-1 text-[12.5px]">
            {active.detail.map(([k, v]) => (
              <React.Fragment key={k}>
                <dt className="text-gray-500">{k}</dt>
                <dd className="m-0 break-words text-gray-900">{v}</dd>
              </React.Fragment>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
};

/** Value-versus-threshold bar: shaded where the rule fires. */
const NearMissBar: React.FC<{ near: RunDiffNearMiss }> = ({ near }) => {
  const max = Math.max(
    near.threshold * 1.5,
    near.value * 1.2,
    near.threshold + 10
  );
  const pct = (n: number) => `${Math.min(100, Math.max(0, (n / max) * 100))}%`;
  const firesBelow = near.op === "<" || near.op === "<=";
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap justify-between gap-2 text-[12.5px]">
        <span>
          <b>{near.title}</b> · {near.account_name} · fires when{" "}
          {near.fires_when}
        </span>
        <span className="whitespace-nowrap font-semibold text-amber-700">
          {near.away_text}
          {near.time_based ? " · moves on its own" : ""}
        </span>
      </div>
      <div
        className="relative h-2.5 rounded bg-gray-100"
        role="img"
        aria-label={`${near.title}: value ${near.value}, fires when ${near.fires_when}`}
      >
        <div
          className="absolute inset-y-0 rounded bg-amber-100"
          style={
            firesBelow
              ? { left: 0, width: pct(near.threshold) }
              : { left: pct(near.threshold), right: 0 }
          }
        />
        <div
          className="absolute -inset-y-1 w-0.5 bg-amber-500"
          style={{ left: pct(near.threshold) }}
        />
        <div
          className="absolute -inset-y-1 -ml-1.5 w-3 rounded-sm bg-brand"
          style={{ left: pct(near.value) }}
        />
      </div>
      <div className="flex justify-between text-[11px] tabular-nums text-gray-400">
        <span>0</span>
        <span>
          threshold {near.threshold} · now {near.value}
        </span>
        <span>{Math.round(max)}</span>
      </div>
    </div>
  );
};

const RunDiffPanel: React.FC<RunDiffPanelProps> = ({
  orgId,
  organizations,
  orgsLoading,
  onOrgChange,
  health,
}) => {
  const dispatch = useAppDispatch();
  const [mode, setMode] = useState<Mode>("back-to-back");
  const [picked, setPicked] = useState<string[]>([]);
  const [diff, setDiff] = useState<RunDiff | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(
    async (params?: Record<string, string>) => {
      if (!orgId) {
        setDiff(null);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const res = await dispatch(
          fetchSignalAdminRunDiff({ orgId, params })
        ).unwrap();
        setDiff(res as RunDiff);
      } catch (e) {
        setError(errorText(e, "Couldn't compare these runs."));
      } finally {
        setLoading(false);
      }
    },
    [dispatch, orgId]
  );

  useEffect(() => {
    setPicked([]);
    void load();
  }, [load]);

  const pickRun = (run: RunSummary) => {
    if (mode === "back-to-back") {
      void load({ to_sweep: run.sweep_id });
      return;
    }
    const next =
      picked.length >= 2 ? [run.sweep_id] : [...picked, run.sweep_id];
    setPicked(next);
    if (next.length === 2) {
      void load({ from_sweep: next[0], to_sweep: next[1] });
    }
  };

  const s = diff?.summary;
  const personaText = s?.personas.length
    ? s.personas
        .map(p => `${p.users} ${PERSONA_LABEL[p.persona] ?? p.persona}`)
        .join(", ")
    : "nobody";

  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="m-0 text-[15px] font-semibold text-gray-900">
            Run Diff
          </h3>
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wider text-gray-500">
            Per organization
          </span>
        </div>
        <p className="m-0 mt-1 max-w-[640px] text-[12.5px] text-gray-600">
          What changed between two sweeps: the cause, the account values that
          moved, the rules that flipped, the cards that appeared or cleared, and
          which personas see a difference. Kept for 60 days.
        </p>
      </div>

      <HealthBanner health={health} />

      <div className="flex flex-wrap items-center gap-3">
        <OrgSelect
          organizations={organizations}
          value={orgId}
          onChange={onOrgChange}
          loading={orgsLoading}
        />
        <div
          className="inline-flex overflow-hidden rounded-full border border-gray-300"
          role="group"
          aria-label="Compare mode"
        >
          {(["back-to-back", "any-two"] as Mode[]).map(m => (
            <button
              key={m}
              type="button"
              onClick={() => {
                setMode(m);
                setPicked([]);
              }}
              className={`px-3 py-1 text-[12px] ${
                mode === m
                  ? "bg-brand-faint font-semibold text-brand"
                  : "text-gray-600"
              }`}
            >
              {m === "back-to-back" ? "Back-to-back runs" : "Any two runs"}
            </button>
          ))}
        </div>
        <span className="text-[11.5px] text-gray-500">
          {mode === "back-to-back"
            ? "Pick a run to compare it with the one before it."
            : picked.length === 1
              ? "Now pick the second run."
              : "Pick two runs to compare."}
        </span>
      </div>

      {!orgId && (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-gray-200 p-8 text-center text-[12.5px] text-gray-500">
          <Building2 className="h-6 w-6 text-gray-300" aria-hidden />
          Select an organization to see what changed between its sweeps.
        </div>
      )}

      {orgId && loading && (
        <div className="flex items-center gap-2 text-[12.5px] text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Comparing
          runs…
        </div>
      )}
      {error && (
        <p className="m-0 rounded-lg bg-red-50 p-3 text-[12.5px] text-red-700">
          {error}
        </p>
      )}

      {orgId && diff?.empty && !loading && (
        <p className="m-0 rounded-lg border border-dashed border-gray-200 p-4 text-[12.5px] text-gray-500">
          No sweeps recorded for this organization yet. Run Diff starts with the
          next sweep (every 5 minutes).
        </p>
      )}

      {orgId && diff && !diff.empty && s && (
        <>
          <div className="flex flex-wrap gap-1.5" aria-label="Runs">
            {[...diff.runs]
              .reverse()
              .slice(-16)
              .map(run => {
                const selected =
                  run.sweep_id === diff.to?.sweep_id ||
                  run.sweep_id === diff.from?.sweep_id ||
                  picked.includes(run.sweep_id);
                return (
                  <button
                    key={run.sweep_id}
                    type="button"
                    onClick={() => pickRun(run)}
                    title={
                      run.quiet
                        ? "Nothing changed"
                        : `${plural(run.accounts_changed, "account")} changed`
                    }
                    className={`rounded-full border px-2.5 py-0.5 text-[11.5px] tabular-nums ${
                      selected
                        ? "border-brand bg-brand-faint font-semibold text-brand"
                        : run.quiet
                          ? "border-gray-200 text-gray-400"
                          : "border-gray-300 text-gray-700"
                    }`}
                  >
                    {timeOf(run, sharesMinute(diff.runs, run))}
                    {!run.quiet && ` · ${run.accounts_changed}`}
                    {run.failed > 0 && " · failed"}
                  </button>
                );
              })}
          </div>

          <p className="m-0 rounded-lg bg-gray-50 p-3 text-[13.5px] leading-relaxed text-gray-800">
            Between{" "}
            <b>
              {timeOf(
                diff.from,
                !!diff.from && sharesMinute(diff.runs, diff.from)
              )}
            </b>{" "}
            and <b>{timeOf(diff.to, sharesMinute(diff.runs, diff.to))}</b>:{" "}
            <b>{plural(s.causes, "cause")}</b> (
            {diff.causes.map(c => c.label).join(", ") || "none"}) flipped{" "}
            <b>
              {plural(s.rule_flips, "rule outcome")} on{" "}
              {plural(s.accounts_changed, "account")}
            </b>
            {!!s.tier_changes && (
              <>
                {" "}
                and changed the tier of <b>{plural(s.tier_changes, "card")}</b>
              </>
            )}
            . Users gained <b>{s.cards_new}</b>{" "}
            {s.cards_new === 1 ? "card" : "cards"}, lost{" "}
            <b>{s.cards_cleared}</b>
            {s.cards_covered > 0 && (
              <>
                , <b>{s.cards_covered}</b> covered by a stronger card
              </>
            )}
            . Who sees a difference: <b>{personaText}</b>.{" "}
            {s.failed_checks
              ? `${plural(s.failed_checks, "check")} failed.`
              : "No failed checks."}
            {s.also_ran.length > 0 &&
              ` Also ran, no effect: ${s.also_ran.join(", ")}.`}
          </p>

          <div className="grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-2">
            {[
              [s.causes, "causes"],
              [s.accounts_changed, "accounts changed"],
              [s.rule_flips, "rule outcomes flipped"],
              ...(s.tier_changes ? [[s.tier_changes, "tier changes"]] : []),
              [`+${s.cards_new} / −${s.cards_cleared}`, "cards new / cleared"],
              [s.cards_covered, "covered"],
              [s.failed_checks, "failed checks"],
            ].map(([n, label]) => (
              <div
                key={String(label)}
                className="rounded-lg border border-gray-200 px-3 py-2"
              >
                <div className="text-[18px] font-semibold tabular-nums text-gray-900">
                  {n}
                </div>
                <div className="text-[11.5px] text-gray-500">{label}</div>
              </div>
            ))}
          </div>

          <FlowDiagram diff={diff} />

          {diff.near_misses.length > 0 && (
            <section className="flex flex-col gap-3 rounded-lg border border-gray-200 p-3">
              <h4 className="m-0 text-[13px] font-semibold text-gray-900">
                Near misses: rules that didn&apos;t fire, but almost did
              </h4>
              <p className="m-0 text-[11.5px] text-gray-500">
                Within 7 days for date-based rules, within 5% of the threshold
                for scores. The shaded zone is where the rule fires; the marker
                is the account&apos;s value now.
              </p>
              {diff.near_misses.slice(0, 8).map(n => (
                <NearMissBar key={`${n.account_id}:${n.rule_id}`} near={n} />
              ))}
            </section>
          )}

          <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-3">
            <section className="rounded-lg border border-gray-200 p-3">
              <h4 className="m-0 mb-1.5 text-[13px] font-semibold text-gray-900">
                What&apos;s kept from users, and why
              </h4>
              <ul className="m-0 flex list-disc flex-col gap-1 pl-4 text-[12.5px] text-gray-700">
                {diff.not_happening.blocked.map(b => (
                  <li key={`b:${b.rule_id}`}>
                    <b>Blocked:</b> {b.title} on {plural(b.accounts, "account")}
                    : data layer off ({b.why}).
                  </li>
                ))}
                {diff.not_happening.not_delivered.map(b => (
                  <li key={`n:${b.rule_id}`}>
                    <b>Not delivered:</b> {b.title} still fires on{" "}
                    {plural(b.accounts, "account")}, but delivery is off.
                  </li>
                ))}
                {diff.not_happening.held.map(h => (
                  <li key={`h:${h.account_name}:${h.rule_id}`}>
                    <b>Held:</b> {h.title} on {h.account_name} stays open until
                    its bands change.
                  </li>
                ))}
                {!diff.not_happening.blocked.length &&
                  !diff.not_happening.not_delivered.length &&
                  !diff.not_happening.held.length && (
                    <li>Nothing is being kept from users.</li>
                  )}
              </ul>
            </section>
            <section className="rounded-lg border border-gray-200 p-3">
              <h4 className="m-0 mb-1.5 text-[13px] font-semibold text-gray-900">
                Pipeline health for this window
              </h4>
              <ul className="m-0 flex list-disc flex-col gap-1 pl-4 text-[12.5px] text-gray-700">
                <li>
                  {plural(diff.health.runs, "run")},{" "}
                  <span
                    className={
                      diff.health.failed_checks
                        ? "font-semibold text-red-700"
                        : ""
                    }
                  >
                    {plural(diff.health.failed_checks, "failed check")}
                  </span>
                  ,{" "}
                  <span
                    className={
                      diff.health.stuck_jobs ? "font-semibold text-red-700" : ""
                    }
                  >
                    {plural(diff.health.stuck_jobs, "job")} stuck
                  </span>
                  .
                </li>
                <li>
                  {plural(diff.health.accounts_not_checked, "account")} not
                  re-checked (no change since the earlier run).
                </li>
                <li>
                  Last daily re-check:{" "}
                  {formatDateTime(diff.health.last_daily_recheck)}.
                </li>
              </ul>
            </section>
          </div>

          {diff.accounts.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full min-w-[760px] border-collapse text-left text-[12.5px]">
                <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Account</th>
                    <th className="px-3 py-2">What changed</th>
                    <th className="px-3 py-2">Rule outcome</th>
                    <th className="px-3 py-2">Card</th>
                    <th className="px-3 py-2">Who sees it</th>
                  </tr>
                </thead>
                <tbody>
                  {diff.accounts.map(a => (
                    <tr
                      key={a.account_id}
                      className="border-t border-gray-100 align-top"
                    >
                      <td className="px-3 py-2 font-semibold text-gray-900">
                        {a.account_name}
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {a.input_changes.length
                          ? a.input_changes.map(c => (
                              <div key={c.field}>
                                {c.label}: {show(c.before)} → {show(c.after)}
                              </div>
                            ))
                          : diff.threshold_changes.length
                            ? diff.threshold_changes.map(c => (
                                <div key={c.field}>
                                  {c.label}: {show(c.before)} → {show(c.after)}
                                </div>
                              ))
                            : "Time passed"}
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {a.flips.map(f => (
                          <div key={f.rule_id} className="mb-1">
                            <b>{f.title}</b>: {f.before_label} → {f.after_label}
                            <div className="text-[11.5px] text-gray-500">
                              {f.explanation}
                            </div>
                          </div>
                        ))}
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {a.cards.length
                          ? a.cards.map(c => (
                              <div key={c.rule_id}>
                                {CARD_CHANGE_LABEL[c.change]} · {c.title}
                                {c.change === "tier" && ` (${cardTierText(c)})`}
                              </div>
                            ))
                          : "No card change"}
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {a.personas.length
                          ? a.personas
                              .map(
                                p =>
                                  `${PERSONA_LABEL[p.persona] ?? p.persona} (${plural(p.users, "user")})`
                              )
                              .join(", ")
                          : "Nobody assigned"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RunDiffPanel;
